import { apiResolver, useApi } from "@/api/useApi";
import { TicketTranscript } from "@/features/ticket-transcript/TicketTranscript";
import { CommentValue } from "@/api/ticket-transcript/TicketTranscript.model";
import { InteractionType } from "@/v2/entities/interaction-in-training/InteractionInTraining.model";
import { ConversationTranscript } from "@/v2/features/interaction-view/conversation-transcript/ConversationTranscript";
import { TicketTranscriptResponse } from "@/v2/features/interaction-view/TicketView";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useQuery } from "react-query";
import { useChat } from "../../context/ChatWrapper";
import { SessionMessages } from "./SessionMessages";
import { useCallback, useState } from "react";
import { CopyPasteCell } from "@/routes/explore/CellGenerator";
import { Dialog } from "@/components/shared/dialog";
import ChainOfThoughts from "../chainOfThoughts/ChainOfThoughts";
import {
  convertAiAgentStepsToChainOfThoughtSteps,
  convertSuggestedReplyToolCallsToChainOfThoughtSteps,
} from "@/v2/utils/aiAgentStepsConverter";

export const TrainingTranscript = ({
  showTranscript,
  handleBack,
}: {
  showTranscript: {
    interactionId: string;
    fetchTranscriptId: string;
    interactionType: InteractionType;
    pendingReviewId?: number;
  } | null;
  handleBack: () => void;
}) => {
  const { api } = useApi();
  const { handleAddMessage } = useChat();

  const [chainOfThoughtsData, setChainOfThoughtsData] = useState<{
    steps: any[];
    contextExport: unknown[];
  } | null>(null);

  const transcriptQuery = useQuery({
    queryKey: ["topics-list", "transcript", showTranscript],
    queryFn: () =>
      apiResolver<TicketTranscriptResponse>(
        api.get(
          `/platform/tickets/${showTranscript?.interactionId}/comments?includeContextExport=true&includeChainOfThoughts=true`
        )
      ),
    enabled:
      !!showTranscript?.interactionId &&
      showTranscript.interactionType === InteractionType.TICKET,
  });

  const handleTicketChainOfThoughtsClick = useCallback(
    (comment: CommentValue) => {
      const chainOfThoughtSteps =
        comment.suggestedReplies?.[0]?.aiResponseThoughts?.[0]?.steps || [];
      const aiAgentSteps = comment.suggestedReplies?.[0]?.steps || [];
      const convertedToolSteps =
        convertAiAgentStepsToChainOfThoughtSteps(aiAgentSteps);
      const suggestedReplyToolCalls =
        comment.suggestedReplies?.[0]?.toolCalls || [];
      const convertedSuggestedReplyToolSteps =
        convertSuggestedReplyToolCallsToChainOfThoughtSteps(
          suggestedReplyToolCalls
        );

      setChainOfThoughtsData({
        steps: [
          ...chainOfThoughtSteps,
          ...convertedToolSteps,
          ...convertedSuggestedReplyToolSteps,
        ],
        contextExport: comment.suggestedReplies?.[0]?.contextExport || [],
      });
    },
    []
  );

  const handleConversationChainOfThoughtsClick = useCallback(
    (source: any) => {
      const steps = source?.chainOfThoughtSteps || [];
      const contextExport = source?.contextExport || [];
      setChainOfThoughtsData({ steps, contextExport });
    },
    []
  );

  const handleChooseMessagesUpTo = (targetCommentId: string) => {
    if (!showTranscript || !transcriptQuery.data?.comments) return;

    const comments = [...transcriptQuery.data.comments].sort((a, b) => {
      if (a.createdAt < b.createdAt) return -1;
      if (a.createdAt > b.createdAt) return 1;
      return 0;
    });
    const targetIndex = comments.findIndex(
      (comment) => comment.externalId === targetCommentId
    );

    if (targetIndex === -1) return;

    const messagesUpToTarget: Array<{
      content: string;
      role: "user" | "assistant";
    }> = comments.slice(0, targetIndex + 1).map((comment) => ({
      content: comment.body,
      role: comment.agent ? "assistant" : "user",
    }));

    handleAddMessage({
      messages: messagesUpToTarget,
      ticketId: showTranscript.interactionId,
      pendingReview: showTranscript.pendingReviewId
        ? {
            id: showTranscript.pendingReviewId,
            interactionType: InteractionType.TICKET,
          }
        : undefined,
    });
  };

  const handleChooseConversationMessagesUpTo = (
    targetMessageId: string,
    messages: Array<{ chatMessageId: string; text: string; author: string }>
  ) => {
    if (!showTranscript || !messages) return;

    const targetIndex = messages.findIndex(
      (message) => message.chatMessageId === targetMessageId
    );

    if (targetIndex === -1) return;

    const messagesUpToTarget: Array<{
      content: string;
      role: "user" | "assistant";
    }> = messages.slice(0, targetIndex + 1).map((message) => ({
      content: message.text,
      role: message.author === "USER" ? "user" : "assistant",
    }));

    handleAddMessage({
      messages: messagesUpToTarget,
      conversationId: showTranscript.interactionId,
      pendingReview: showTranscript.pendingReviewId
        ? {
            id: showTranscript.pendingReviewId,
            interactionType: InteractionType.CONVERSATION,
          }
        : undefined,
    });
  };

  const handleChooseSessionMessagesUpTo = (
    messages: Array<{ chatMessageId: string; text: string; author: string }>
  ) => {
    if (!showTranscript || !messages) return;

    const messagesUpToTarget: Array<{
      content: string;
      role: "user" | "assistant";
    }> = messages.map((message) => ({
      content: message.text,
      role: message.author === "USER" ? "user" : "assistant",
    }));

    handleAddMessage({
      messages: messagesUpToTarget,
    });
  };

  return (
    <>
      <Flex gap="2" p="2" pb="0" justify="between">
        <Button
          variant="ghost"
          size="2"
          onClick={handleBack}
          style={{ alignSelf: "flex-start" }}
        >
          ← Back
        </Button>
        <Text size="1" as="div" className="flex items-center gap-2">
          {showTranscript?.interactionType === InteractionType.TICKET
            ? "Ticket ID"
            : showTranscript?.interactionType === InteractionType.CONVERSATION
            ? "Conversation ID"
            : "Session ID"}{" "}
          <CopyPasteCell
            value={
              showTranscript?.interactionId?.includes("::")
                ? showTranscript?.interactionId.split("::")[1]
                : showTranscript?.fetchTranscriptId ?? ""
            }
            truncate
          />
        </Text>
      </Flex>

      {showTranscript?.interactionType === InteractionType.TICKET ? (
        <TicketTranscript
          data={transcriptQuery.data?.comments}
          isLoading={transcriptQuery.isLoading}
          collaborators={[]}
          chooseComment={(commentId: string, comment: string) => {
            if (!showTranscript) return;

            handleAddMessage({
              messages: [
                {
                  content: comment,
                  role: "user",
                },
              ],
              ticketId: showTranscript.interactionId,
              pendingReview: showTranscript.pendingReviewId
                ? {
                    id: showTranscript.pendingReviewId,
                    interactionType: InteractionType.TICKET,
                  }
                : undefined,
            });
          }}
          chooseMessagesUpTo={handleChooseMessagesUpTo}
          onChainOfThoughtsClick={handleTicketChainOfThoughtsClick}
        />
      ) : showTranscript?.interactionType === InteractionType.CONVERSATION &&
        showTranscript?.fetchTranscriptId.startsWith("session-") ? (
        <SessionMessages
          session={JSON.parse(
            showTranscript.fetchTranscriptId.replace("session-", "")
          )}
          onTrain={handleChooseSessionMessagesUpTo}
          onTrainUpTo={handleChooseSessionMessagesUpTo}
        />
      ) : (
        <ConversationTranscript
          conversation={{
            id: showTranscript?.interactionId ?? "",
            displayId: showTranscript?.fetchTranscriptId ?? "",
          }}
          onChainOfThoughtsClick={handleConversationChainOfThoughtsClick}
          onTrain={(messageId: string, message: string) => {
            if (!showTranscript) return;

            handleAddMessage({
              messages: [
                {
                  content: message,
                  role: "user",
                },
              ],
              conversationId: showTranscript.interactionId,
              pendingReview: showTranscript.pendingReviewId
                ? {
                    id: showTranscript.pendingReviewId,
                    interactionType: InteractionType.CONVERSATION,
                  }
                : undefined,
            });
          }}
          chooseMessagesUpTo={(
            targetMessageId: string,
            messages: Array<{
              chatMessageId: string;
              text: string;
              author: string;
            }>
          ) => handleChooseConversationMessagesUpTo(targetMessageId, messages)}
        />
      )}

      <Dialog
        open={chainOfThoughtsData !== null}
        onOpenChange={(open) => {
          if (!open) setChainOfThoughtsData(null);
        }}
      >
        <Dialog.Content
          size="medium"
          style={{ width: "fit-content", minWidth: "450px", maxWidth: "780px" }}
        >
          <Dialog.Title>
            Chain of Thought
            <Dialog.Close />
          </Dialog.Title>
          <Flex direction="column" px="5" pb="5" pt="2" style={{ overflow: "auto", maxHeight: "65vh" }}>
              {chainOfThoughtsData && (
                <ChainOfThoughts
                  title="Chain of thought"
                  steps={chainOfThoughtsData.steps}
                  defaultOpen={true}
                  currentStepIndex={-1}
                  sources={
                    Array.isArray(chainOfThoughtsData.contextExport)
                      ? chainOfThoughtsData.contextExport
                      : []
                  }
                  isLoading={false}
                  hasTopic={true}
                  collapsible={false}
                />
              )}
          </Flex>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
